import { useSelector } from 'react-redux';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { differenceInSeconds } from 'date-fns';
import { utcToZonedTime, format as tzFormat } from 'date-fns-tz';
import { useDecision } from '@optimizely/react-sdk';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { getEndTime } from '@/common/countdownTimer/getTimerDetails';

interface CountdownTimerProps {
  duration: string; // format: HH:mm:ss
  intervalStartTime: string; // format: HH:mm:ss
  dealName: string;
}

interface CountdownTimerReturn {
  timerText: string;
  hours: string;
  minutes: string;
  seconds: string;
  isCompleted: boolean;
  screenReaderText: string;
  timerAccessibilityEnabled: boolean;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

const useCountdownTimer = ({ duration, intervalStartTime, dealName }: CountdownTimerProps): CountdownTimerReturn => {
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const storeTimezone = storeDetails?.storeTimezone;

  const [{ enabled: timerAccessibilityEnabled }] = useDecision('fr-DTG-1771-timer-accessibility');

  const [hasAnnounced, setHasAnnounced] = useState<boolean>(false);
  const [screenReaderText, setScreenReaderText] = useState<string>('');

  // Helper to get the end time as a Date object in the store's timezone
  const endTime = useCallback(() => getEndTime(storeTimezone, intervalStartTime, duration),[duration, intervalStartTime, storeTimezone]);

  const [timeLeft, setTimeLeft] = useState(() => {
    const end = endTime();
    const now = utcToZonedTime(new Date(), storeTimezone);
    const diff = differenceInSeconds(end, now);
    return diff > 0 ? diff : 0;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const end = endTime();
      const now = utcToZonedTime(new Date(), storeTimezone);
      const diff = differenceInSeconds(end, now);
      setTimeLeft(diff > 0 ? diff : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, [duration, intervalStartTime, storeTimezone, endTime]);

  // Format timer text (end time in store's timezone, e.g. 'only til 5 PM')
  const timerText = useMemo(() => {
    const end = endTime();
    return `only til ${tzFormat(end, 'h a', { timeZone: storeTimezone })}`;
  }, [storeTimezone, endTime]);

  // Convert seconds left to hh:mm:ss
  const hours = pad2(Math.floor(timeLeft / 3600));
  const minutes = pad2(Math.floor((timeLeft % 3600) / 60));
  const seconds = pad2(timeLeft % 60);

  useEffect(() => {
    if (timerAccessibilityEnabled && timeLeft > 0 && !hasAnnounced) {
      let announcement = '';

      const numHours = +hours;
      const numMinutes = +minutes;
      const numSeconds = +seconds;
      
      if (numHours > 0) {
        const hourText = numHours === 1 ? 'hour' : 'hours';
        announcement += `${hours} ${hourText}`;
      }

      if (numMinutes > 0) {
        const minuteText = numMinutes === 1 ? 'minute' : 'minutes';
        if (announcement) {
          announcement += ' and ';
        }
        announcement += `${minutes} ${minuteText}`;
      }

      if (!announcement && numSeconds > 0) {
        const secondText = numSeconds === 1 ? 'second' : 'seconds';
        announcement = `${seconds} ${secondText}`;
      }
      
      if (announcement) {
        setScreenReaderText(`${dealName}: ${announcement} remaining`);
        setHasAnnounced(true);
      }
    }
  }, [timeLeft, hasAnnounced, hours, minutes, seconds, timerAccessibilityEnabled, dealName]);

  return {
    timerText,
    hours,
    minutes,
    seconds,
    isCompleted: timeLeft === 0,
    screenReaderText,
    timerAccessibilityEnabled
  };
};

export default useCountdownTimer;
